import React from 'react';
import { useWatch } from 'react-hook-form';

import { Component, ComponentLaunchStatus, ComponentSection } from 'models';

import { ApplicationField } from 'components/component-fields/ApplicationField';
import { LaunchStatusField } from 'components/component-fields/LaunchStatusField';
import { RegionAvailabilityField } from 'components/component-fields/RegionAvailability';
import { ReleaseQuarterField } from 'components/component-fields/ReleaseQuarterField';
import { Datasheet } from 'components/datasheet/Datasheet';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { CheckboxField } from 'components/forms/fields/CheckboxField';
import { DateField } from 'components/forms/fields/DateField';
import { ManufacturerField } from 'components/forms/fields/ManufacturerField';
import { MultilineTextField } from 'components/forms/fields/MultilineTextField';
import { NumberField } from 'components/forms/fields/NumberField';
import { PriceField } from 'components/forms/fields/PriceField';
import { TextField } from 'components/forms/fields/TextField';
import { URLField } from 'components/forms/fields/URLField';

import { ComponentDatasheetSectionProgress } from '../ComponentDatasheetSectionProgress';
import { ComponentDatasheetSection } from './ComponentDatasheetSection';

const General = () => {
    const mode = useDatasheetMode();

    const compliance = useWatch({
        name: 'compliance',
    }) as Component['compliance'];

    const launchStatus = useWatch({
        name: 'lifecycle.launchStatus',
    }) as ComponentLaunchStatus | undefined;

    if (mode === DatasheetMode.VIEW) {
        return null;
    }

    return (
        <ComponentDatasheetSection
            id={ComponentSection.GENERAL}
            title="General"
            afterTitle={<ComponentDatasheetSectionProgress section={ComponentSection.GENERAL} />}
            alwaysShow
        >
            <Datasheet.Table header={{ label: 'General' }} isPublic>
                <Datasheet.TableRow name="name" label="Name">
                    <TextField name="name" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="description" label="Description">
                    <MultilineTextField name="description" autosize minRows={1} />
                </Datasheet.TableRow>
                <Datasheet.TableRow
                    name="specificationsSummary"
                    label="Specifications Summary"
                    description="Automatically generated from the component specifications. You can override what is shown on the datasheet by filling in a description."
                >
                    <MultilineTextField name="specificationsSummary" autosize minRows={1} readOnly />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="manufacturer" label="Manufacturer">
                    <ManufacturerField name="manufacturer" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="productIdentifier" label="Part Number">
                    <TextField name="productIdentifier" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="productSeries" label="Product Series">
                    <TextField name="productSeries" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="msrp" label="Avg. Price">
                    <PriceField name="msrp" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="leadTime" label="Typ. Lead Time (in days)">
                    <NumberField name="leadTime" allowNegative={false} />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="lifecycle.launchStatus" label="Launch Timing">
                    <LaunchStatusField name="lifecycle.launchStatus" />
                </Datasheet.TableRow>
                {launchStatus === ComponentLaunchStatus.QUARTER && (
                    <Datasheet.TableRow name="lifecycle.releaseQuarter" label="Release Quarter">
                        <ReleaseQuarterField name="lifecycle.releaseQuarter" />
                    </Datasheet.TableRow>
                )}
                {launchStatus !== ComponentLaunchStatus.QUARTER &&
                    launchStatus !== ComponentLaunchStatus.COMING_SOON && (
                        <Datasheet.TableRow name="lifecycle.release" label="Release Date">
                            <DateField name="lifecycle.release" />
                        </Datasheet.TableRow>
                    )}
                <Datasheet.TableRow name="lifecycle.endOfLife" label="End of Life Date">
                    <DateField name="lifecycle.endOfLife" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="website" label="Website">
                    <URLField name="website" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="regionAvailability" label="Region Availability">
                    <RegionAvailabilityField name="regionAvailability" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="application" label="Application">
                    <ApplicationField name="application" />
                </Datasheet.TableRow>
                <Datasheet.TableRow
                    name="publicSpecifications"
                    label="Public Specifications"
                    description="By default visitors need an account to see the full specifications. Enable this to show them to everyone, for example when sharing a QR code at an event."
                >
                    <CheckboxField name="publicSpecifications" label="Visible without an account" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.CE" label="CE Compliance">
                    <CheckboxField name="compliance.CE" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.UL" label="UL Compliance">
                    <CheckboxField name="compliance.UL" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.CSA" label="CSA Certification">
                    <CheckboxField name="compliance.CSA" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.CCC" label="CCC Certification">
                    <CheckboxField name="compliance.CCC" />
                </Datasheet.TableRow>
                {/* For now, only show these on profile */}
                {/* <Datasheet.TableRow name="compliance.currentOS" label="CurrentOS">
                    <CheckboxField name="compliance.currentOS" label="We are a member of CurrentOS" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.emergeAlliance" label="EMerge Alliance">
                    <CheckboxField name="compliance.emergeAlliance" label="We are a member of EMerge Alliance" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.ODCA" label="ODCA">
                    <CheckboxField name="compliance.ODCA" label="We are a member of ODCA" />
                </Datasheet.TableRow>
                <Datasheet.TableRow name="compliance.other" label="Other">
                    <CheckboxField
                        name="compliance.other"
                        label="We are a member of another DC Microgrid Organization"
                    />
                </Datasheet.TableRow> */}

                {compliance?.other && (
                    <Datasheet.TableRow name="compliance.otherInput" label="Specify other">
                        <TextField name="compliance.otherInput" />
                    </Datasheet.TableRow>
                )}
            </Datasheet.Table>
        </ComponentDatasheetSection>
    );
};

export { General };
